import { Component, computed, inject, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { httpResource } from "@angular/common/http"
import { Router } from "@angular/router"
import { firstValueFrom } from "rxjs"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatFormField } from "@angular/material/form-field"
import { MatSelect, MatOption } from "@angular/material/select"
import { FormField, form, submit } from "@angular/forms/signals"
import { CATEGORY_FIELD_LABELS, Category, CreateCategory, CreateCategoryFactory } from "../../models"
import { CategoryService, ToastService } from "../../services"
import { TableComponent } from "../../components/table/table.component"
import { invalidFieldsDetail, toServerFieldErrors } from "../../helpers/FormErrors"
import { BooleanInputComponent } from "../../components/boolean-input/boolean-input.component"
import {
    DictionaryInputComponent,
} from "../../components"

@Component({
    selector: "valben-categories-add",
    templateUrl: "./categories-add.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        NgClass,
        MatProgressSpinnerModule,
        MatFormField,
        MatSelect,
        MatOption,
        FormField,
        DictionaryInputComponent,
        BooleanInputComponent,
    ],
})
export class CategoriesAddPage {
    private _categoryService = inject(CategoryService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)

    saving = signal(false)

    model = signal<CreateCategory>(CreateCategoryFactory.create())
    categoryForm = form(this.model)

    typeOptions = [
        { value: "Award", display: "Nagrada" },
        { value: "Perk", display: "Pogodnost" },
        { value: "Benefit", display: "Benefit" },
    ]

    categories = httpResource<Category[]>(() => this._categoryService.AllUrl())

    parentOptions = computed(() => {
        const type = this.categoryForm.type().value().toLowerCase()
        return (this.categories.value() ?? [])
            .filter(c => c.type.toLowerCase() === type)
            .map(c => ({ masterId: c.masterId, name: c.names?.["hr"] ?? "" }))
    })

    setType(type: string): void {
        this.categoryForm.type().value.set(type)
        const parentId = this.categoryForm.parentId().value()
        if (parentId && !this.parentOptions().some(o => o.masterId === parentId)) {
            this.categoryForm.parentId().value.set("")
        }
    }

    save(): void {
        this._create(false)
    }

    draft(): void {
        this._create(true)
    }

    back(): void {
        this._router.navigate(["/categories"])
    }

    private async _create(isDraft: boolean): Promise<void> {
        this.categoryForm.isDraft().value.set(isDraft)
        this.saving.set(true)
        let failed = false

        const created = await submit(this.categoryForm, {
            action: async f => {
                try {
                    await firstValueFrom(this._categoryService.Create(f().value()))
                    return null
                } catch (err) {
                    failed = true
                    const { detail, errors } = toServerFieldErrors(err, this.categoryForm, CATEGORY_FIELD_LABELS)
                    this._error(detail)
                    return errors
                }
            },
            onInvalid: () => this._error(invalidFieldsDetail(this.categoryForm, CATEGORY_FIELD_LABELS)),
        })

        this.saving.set(false)
        if (failed || !created) return

        this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Uspješno uneseno",
            life: 10000,
        })
        TableComponent.resetPageOffset()
        this.back()
    }

    private _error(detail: string): void {
        this._messageService.add({ severity: "error", summary: "Error", detail, life: 10000 })
    }
}
